/**
 * Fallback quando a sessão Sanctum morreu: limpa JWT e redireciona ao login.
 * Também notifica outras abas via localStorage (sessionStorage é por aba).
 */
import { clearJwtLocalSession } from './localSession';

const REDIRECT_FLAG = '__isendSessionExpiredRedirecting';
const SESSION_EXPIRED_STORAGE_KEY = 'isend.session_expired_at';

/**
 * Rotas publicas (requiresAuth: false) que nao devem sofrer redirect por 401.
 * Recuperacao de senha roda sem sessao: um 401 ali e esperado, nao expiracao.
 */
const LOGIN_HASH_PATHS = ['/', '/login'];
const PASSWORD_RECOVERY_HASH_PATHS = [
  '/usuario/senha/esqueceu',
  '/usuario/senha/reset',
];

/**
 * Path da rota atual, normalizado (`#/x/?a=1` -> `/x`).
 * Sem rota no hash, cai no pathname: link de e-mail pode chegar com o `#`
 * escapado (`/%23/usuario/senha/reset`) e a normalizacao pode nao ter rodado.
 */
function currentHashPath() {
  const loc = globalThis.window.location;
  const hash = (loc.hash || '').replace(/^#/, '');
  let raw = (hash.split('?')[0] || '').trim();

  if (!raw || raw === '/') {
    let pathname = loc.pathname || '/';
    try {
      pathname = decodeURIComponent(pathname);
    } catch (error) {
      /* escape invalido: usa o valor original */
    }
    raw = '/' + pathname.replace(/^[/#]+/, '');
  }

  return raw.replace(/\/+$/, '').toLowerCase();
}

/** Telas de recuperacao de senha: rodam sem sessao por definicao. */
function isOnPasswordRecoveryPath() {
  try {
    return PASSWORD_RECOVERY_HASH_PATHS.indexOf(currentHashPath()) !== -1;
  } catch (error) {
    return false;
  }
}

/**
 * Home/login do legado (path `/` sem rota de app no hash, ou `/login`) e as
 * telas de recuperacao de senha. Rotas autenticadas usam hash (`#/dashboard`).
 */
function isOnPublicPath() {
  try {
    const hashPath = currentHashPath();
    if (!hashPath) {
      return true;
    }
    return (
      LOGIN_HASH_PATHS.indexOf(hashPath) !== -1 ||
      PASSWORD_RECOVERY_HASH_PATHS.indexOf(hashPath) !== -1
    );
  } catch (error) {
    return false;
  }
}

function clearSessionExpiredFlag() {
  try {
    delete globalThis[REDIRECT_FLAG];
    if (globalThis.window) {
      globalThis.window._redirectToastShown = false;
    }
  } catch (error) {
    /* ignore */
  }
}

/**
 * Avisa outras abas do mesmo origin que a sessão caiu.
 * `storage` só dispara nas *outras* abas (não na que escreveu).
 */
function broadcastSessionExpired() {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, String(Date.now()));
  } catch (error) {
    /* private mode / quota */
  }
}

/**
 * Limpa JWT local e redireciona para a origem (tela de login do legado).
 * Idempotente via flag global — a flag só é setada quando o redirect de fato
 * será agendado. Toast nunca pode impedir o redirect (try/catch + schedule
 * antes). 401 na tela de login não prende a flag (login usa SPA push).
 */
export function forceSessionExpired(options) {
  const opts = options || {};
  const showToast = opts.showToast !== false;
  const broadcast = opts.broadcast !== false;

  clearJwtLocalSession();

  try {
    if (globalThis.window && globalThis.window.__store && globalThis.window.__store.state) {
      globalThis.window.__store.state.logged = {
        user: null,
        permissions: [],
        bclientes: [],
        notificacao: [],
      };
    }
  } catch (error) {
    /* store pode não estar pronto */
  }

  if (isOnPublicPath()) {
    clearSessionExpiredFlag();
    return;
  }

  if (globalThis[REDIRECT_FLAG]) {
    return;
  }
  globalThis[REDIRECT_FLAG] = true;

  if (broadcast) {
    broadcastSessionExpired();
  }

  let toastShown = false;
  try {
    if (
      showToast &&
      globalThis.window &&
      globalThis.window.Vue &&
      globalThis.window.Vue.prototype.$snotify &&
      !globalThis.window._redirectToastShown
    ) {
      globalThis.window._redirectToastShown = true;
      globalThis.window.Vue.prototype.$snotify.warning(
        'Sua sessão expirou. Você será redirecionado para o login.'
      );
      toastShown = true;
    }
  } catch (error) {
    toastShown = false;
  }

  // Redirect sempre agenda — toast não pode engolir o fluxo.
  const delay = toastShown ? 800 : 0;
  setTimeout(() => {
    try {
      globalThis.window.location.replace(globalThis.window.location.origin + '/');
    } catch (error) {
      try {
        globalThis.window.location.href = globalThis.window.location.origin + '/';
      } catch (e2) {
        /* ignore */
      }
    }
  }, delay);
}

/**
 * Escuta logout/expiração feitos em outra aba do mesmo origin.
 */
export function bindSessionExpiredCrossTabListener() {
  try {
    if (typeof window === 'undefined' || window.__isendSessionExpiredListenerBound) {
      return;
    }
    window.__isendSessionExpiredListenerBound = true;
    window.addEventListener('storage', (event) => {
      if (!event || event.key !== SESSION_EXPIRED_STORAGE_KEY) {
        return;
      }
      if (!event.newValue) {
        return;
      }
      // Outra aba já derrubou a sessão — redireciona sem re-broadcast.
      forceSessionExpired({ showToast: false, broadcast: false });
    });
  } catch (error) {
    /* ignore */
  }
}

/** Só para testes / reauth bem-sucedida. */
export function resetForceSessionExpiredState() {
  clearSessionExpiredFlag();
}

export { SESSION_EXPIRED_STORAGE_KEY, isOnPublicPath, isOnPasswordRecoveryPath };
